import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import {
  COLLAPSED_BY_DEFAULT_SOURCE_TYPES,
  DOCUMENT_SOURCE_TITLE_KEYS,
  composeSourcePath
} from './fabricationDocumentSources'
import FabricationDocumentEntryRow from './FabricationDocumentEntryRow'

// Historia 148 — la LISTA del panel izquierdo: un grupo por tipo de procedencia y, dentro, una
// cabecera por REGISTRO. Ambos niveles se pliegan.
//
// ⚠️ UN REGISTRO, UN PLEGABLE. Un job tiene exactamente UNA orden de fabricación, UNA referencia
// de pieza, UN proceso y UN job: en esos grupos la cabecera de registro repetía lo que ya dice la
// del grupo. Cuando el grupo trae un solo registro, su código y su nombre suben a la cabecera del
// grupo y desaparece el segundo nivel. La regla se deriva de los DATOS, no de una lista fija de
// tipos: si algún día un grupo trajera dos registros, los dos niveles vuelven solos.
//
// ⚠️ QUÉ ES UN REGISTRO TRAS LA ENMIENDA E1 (RN38/S93/S94): ya NO basta el `sourceReferenceId`. La
// misma feature alcanzada desde dos tareas llega dos veces, con idéntico código, nombre e
// identidad, y lo único que las distingue es la RUTA. Si el registro se agrupara solo por
// identidad, las dos apariciones se fundirían en una y el operario perdería de qué tarea viene cada
// documento — y plegar una plegaría la otra. La clave de registro es identidad + ruta.
//
// ⚠️ LA RUTA SE COMPONE AQUÍ (RN37/S93). Sun manda los TRAMOS; el separador y qué se pinta de cada
// uno los decide el puesto. Se pinta en la cabecera del registro, no como árbol: el modal sigue
// siendo una lista plana de grupos hermanos (opción B de la enmienda).
//
// ⚠️ PLEGADO. Todo empieza ABIERTO salvo los dos tipos que la enmienda añade (RN39/S94): una
// feature alcanzada desde cada tarea multiplica las cabeceras y con todo abierto la columna se
// vuelve inmanejable. Los 7 originales no cambian — S50 sigue verde.
//
// ⚠️ LA BÚSQUEDA MANDA SOBRE EL PLEGADO (S95). Un resultado escondido tras una cabecera plegada es
// un resultado que el operario no encuentra, y le haría concluir que el documento no existe.
// Mientras hay texto buscado todo se muestra; al borrarlo vuelve a mandar el plegado — el que él
// eligió y el que traen por defecto los dos grupos nuevos.

const GROUP_TITLE_SX = { fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }
const HEADER_SX = {
  width: '100%',
  justifyContent: 'flex-start',
  gap: 1,
  px: 1,
  py: 0.5,
  borderRadius: 1,
  // Área táctil de taller también en las cabeceras (RN30/D18/S94).
  minHeight: 48
}
const RECORD_HEADER_SX = { ...HEADER_SX, bgcolor: 'action.hover' }

const chevronSx = (expanded) => ({
  transform: expanded ? 'none' : 'rotate(-90deg)',
  transition: 'transform 0.2s',
  flexShrink: 0
})

/**
 * Agrupa las entradas de un grupo por su registro, conservando el orden en que llegaron (RN13).
 * El registro es identidad + RUTA: la misma feature alcanzada desde dos tareas son dos registros.
 */
const byRecord = (entries) => {
  const records = []
  entries.forEach((entry) => {
    const sourcePath = composeSourcePath(entry.sourcePath)
    const existing = records.find(
      (record) => record.sourceReferenceId === entry.sourceReferenceId && record.sourcePath === sourcePath
    )
    if (existing) {
      existing.entries.push(entry)
      return
    }
    records.push({
      sourceReferenceId: entry.sourceReferenceId,
      sourceCode: entry.sourceCode,
      sourceName: entry.sourceName,
      sourcePath,
      entries: [entry]
    })
  })
  return records
}

const FabricationDocumentGroupList = ({
  groups = [],
  selectedKey = '',
  forceExpanded = false,
  onView = () => {},
  onDownload = () => {},
  onEdit = () => {},
  onDelete = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  // Lo que el operario ha plegado o desplegado A MANO. Vacío al abrir: manda el valor por defecto.
  const [overrides, setOverrides] = useState({})

  const isExpanded = (key, expandedByDefault) => {
    if (forceExpanded) {
      return true
    }
    return key in overrides ? overrides[key] : expandedByDefault
  }
  const toggle = (key, expandedByDefault) => setOverrides((previous) => ({
    ...previous,
    [key]: !(key in previous ? previous[key] : expandedByDefault)
  }))

  const renderEntry = (group, entry) => (
    <FabricationDocumentEntryRow
      // El uuid NO basta como clave: el mismo documento puede repetirse bajo dos registros del
      // mismo grupo. La clave es la aparición, no el documento.
      key={group.sourceType + '-' + entry.sourceReferenceId + '-' + composeSourcePath(entry.sourcePath) + '-' + entry.document?.uuid}
      sourceType={group.sourceType}
      entry={entry}
      permissions={group.permissions}
      selected={selectedKey === group.sourceType + '-' + entry.sourceReferenceId + '-' + entry.document?.uuid}
      onView={(clickedEntry, source) => onView(clickedEntry, source, group.permissions)}
      onDownload={onDownload}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  )

  const renderRecordLabel = (record) => (
    <>
      <Chip size='small' color='primary' label={record.sourceCode} />
      {record.sourceName && record.sourceName !== record.sourceCode
        ? (
          <Typography variant='body2' color='text.secondary' noWrap sx={{ minWidth: 0 }}>
            {record.sourceName}
          </Typography>
          )
        : null}
      {/* RN37/S93: de qué tarea —y de qué grupo de feature— viene este registro. Vacía en los 7
          tipos originales, donde no se pinta nada. */}
      {record.sourcePath !== ''
        ? (
          <Typography variant='caption' color='text.secondary' noWrap sx={{ minWidth: 0 }}>
            {record.sourcePath}
          </Typography>
          )
        : null}
    </>
  )

  return (
    <Stack spacing={2} sx={{ width: '100%', textAlign: 'left' }}>
      {groups.map((group) => {
        const groupKey = group.sourceType
        const documentCount = group.entries.length
        const records = byRecord(group.entries)
        // Cadena vacía = el grupo trae VARIOS registros y necesita su segundo nivel.
        const onlyRecord = records.length === 1 ? records[0] : ''
        const recordsExpandedByDefault = !COLLAPSED_BY_DEFAULT_SOURCE_TYPES.includes(group.sourceType)
        // ⚠️ UN INTERRUPTOR, UN ESTADO. Cuando el grupo trae un solo registro su rótulo sube a la
        // cabecera y el segundo nivel desaparece: entonces esta cabecera ES la del registro, y manda
        // el valor por defecto DEL REGISTRO. Con dos estados —el del grupo, abierto; el del registro,
        // plegado en los dos tipos nuevos— el grupo se pintaba desplegado y vacío, y no quedaba
        // ningún control que pulsar para abrirlo: el operario concluía que la feature no tenía
        // documentos aunque el contador dijera que sí.
        const groupExpanded = isExpanded(groupKey, onlyRecord === '' ? true : recordsExpandedByDefault)

        return (
          <Box
            key={group.sourceType}
            component='section'
            data-testid={'document-group-' + group.sourceType}
            data-source-type={group.sourceType}
          >
            <ButtonBase
              data-testid={'group-toggle-' + group.sourceType}
              aria-expanded={groupExpanded}
              onClick={() => toggle(groupKey, onlyRecord === '' ? true : recordsExpandedByDefault)}
              sx={HEADER_SX}
            >
              <ExpandMoreIcon fontSize='small' sx={chevronSx(groupExpanded)} />
              <Typography variant='overline' color='primary' sx={GROUP_TITLE_SX}>
                {t_(DOCUMENT_SOURCE_TITLE_KEYS[group.sourceType] || group.sourceType)}
              </Typography>
              {/* Con un solo registro, su identidad sube aquí: no hace falta una fila más para decir
                  de qué pieza son estos documentos. */}
              {onlyRecord !== '' ? renderRecordLabel(onlyRecord) : null}
              {/* El contador acompaña a la cabecera para que, plegada, siga diciendo cuánto esconde. */}
              <Chip size='small' variant='outlined' label={documentCount} sx={{ ml: 'auto' }} />
            </ButtonBase>

            {/* E10/RN41.2/S96 — el grupo viaja por ser DESTINO de alta, sin un solo documento. Se
                dice explícitamente: ni fila vacía, ni contador a cero, ni cabecera de registro sin
                contenido. Y su literal es distinto del de «esta fabricación no tiene documentación»
                y del de «sin resultados» de la búsqueda. */}
            {groupExpanded && group.entries.length === 0
              ? (
                <Typography variant='body2' color='text.secondary' sx={{ px: 1, py: 0.5 }}>
                  {t_('This record has no documents yet')}
                </Typography>
                )
              : null}

            {groupExpanded && onlyRecord !== ''
              ? (
                <Box
                  data-testid='document-record'
                  data-source-reference-id={String(onlyRecord.sourceReferenceId)}
                  data-source-path={onlyRecord.sourcePath}
                >
                  <Box component='ul' sx={{ m: 0, p: 0 }}>
                    {onlyRecord.entries.map((entry) => renderEntry(group, entry))}
                  </Box>
                </Box>
                )
              : null}

            {groupExpanded && onlyRecord === ''
              ? records.map((record) => {
                const recordKey = group.sourceType + '-' + record.sourceReferenceId + '-' + record.sourcePath
                const recordExpanded = isExpanded(recordKey, recordsExpandedByDefault)

                return (
                  <Box
                    key={recordKey}
                    data-testid='document-record'
                    data-source-reference-id={String(record.sourceReferenceId)}
                    data-source-path={record.sourcePath}
                    sx={{ mb: 1 }}
                  >
                    <ButtonBase
                      data-testid={'record-toggle-' + record.sourceReferenceId}
                      aria-expanded={recordExpanded}
                      onClick={() => toggle(recordKey, recordsExpandedByDefault)}
                      sx={RECORD_HEADER_SX}
                    >
                      <ExpandMoreIcon fontSize='small' sx={chevronSx(recordExpanded)} />
                      {renderRecordLabel(record)}
                      <Chip size='small' variant='outlined' label={record.entries.length} sx={{ ml: 'auto' }} />
                    </ButtonBase>

                    {recordExpanded
                      ? (
                        <Box component='ul' sx={{ m: 0, p: 0 }}>
                          {record.entries.map((entry) => renderEntry(group, entry))}
                        </Box>
                        )
                      : null}
                  </Box>
                )
              })
              : null}
          </Box>
        )
      })}
    </Stack>
  )
}

FabricationDocumentGroupList.propTypes = {
  groups: PropTypes.array,
  selectedKey: PropTypes.string,
  forceExpanded: PropTypes.bool,
  onView: PropTypes.func,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
}

export { byRecord }
export default FabricationDocumentGroupList
